import { Injectable, inject, isDevMode } from '@angular/core';

import { LOG_LEVEL_PRIORITY, type LoggerConfig } from './logger.config';
import { LOGGER_CONFIG } from './logger.config.token';
import { ScopedLogger } from './scoped-logger';
import { type LogContext, type LogLevel } from './logger.types';

@Injectable({ providedIn: 'root' })
export class LoggerProvider {
  private readonly _isDevelopment = isDevMode();
  private _config: LoggerConfig = {
    enableProductionLogging: true,
    minLogLevel: isDevMode() ? 'debug' : 'error',
    globalPrefix: '[CM]',
  };

  constructor() {
    const override = inject(LOGGER_CONFIG, { optional: true }) ?? {};
    this._config = { ...this._config, ...override };
  }

  public debug(message: string, context?: LogContext): void {
    if (!this._shouldLog('debug')) return;
    const [msg, ...args] = this._formatMessage(message, context);
    console.debug(msg, ...args);
  }

  public log(message: string, context?: LogContext): void {
    if (!this._shouldLog('log')) return;
    const [msg, ...args] = this._formatMessage(message, context);
    console.log(msg, ...args);
  }

  public info(message: string, context?: LogContext): void {
    if (!this._shouldLog('info')) return;
    const [msg, ...args] = this._formatMessage(message, context);
    console.info(msg, ...args);
  }

  public warn(message: string, context?: LogContext): void {
    if (!this._shouldLog('warn')) return;
    const [msg, ...args] = this._formatMessage(message, context);
    console.warn(msg, ...args);
  }

  public error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (!this._shouldLog('error')) return;
    const [msg, ...args] = this._formatMessage(message, context);
    if (error !== undefined) {
      console.error(msg, error, ...args);
    } else {
      console.error(msg, ...args);
    }
  }

  public logWithLevel(level: LogLevel, message: string, context?: LogContext): void {
    switch (level) {
      case 'debug':
        this.debug(message, context);
        break;
      case 'log':
        this.log(message, context);
        break;
      case 'info':
        this.info(message, context);
        break;
      case 'warn':
        this.warn(message, context);
        break;
      case 'error':
        this.error(message, undefined, context);
        break;
    }
  }

  public createScoped(scope: string): ScopedLogger {
    return new ScopedLogger(this, scope);
  }

  public enableProductionLogging(enable: boolean): void {
    this._config = { ...this._config, enableProductionLogging: enable };
  }

  private get _isLoggingEnabled(): boolean {
    if (this._isDevelopment) {
      return true;
    }
    return this._config.enableProductionLogging || this._isRuntimeOverrideEnabled();
  }

  private _isRuntimeOverrideEnabled(): boolean {
    if (typeof window === 'undefined') return false;
    // URL param is dev-only: shared/proxied URLs could expose it in production
    if (isDevMode()) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('debug') === 'true') return true;
    }
    // Window flag requires console access, so it remains usable in production
    return (window as unknown as Record<string, unknown>)['CM_DEBUG_LOGGING'] === true;
  }

  private _shouldLog(level: LogLevel): boolean {
    if (!this._isLoggingEnabled) {
      return false;
    }
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this._config.minLogLevel];
  }

  private _formatMessage(message: string, context?: LogContext): [string, ...unknown[]] {
    const formatted = `${this._config.globalPrefix} ${message}`;
    if (context && Object.keys(context).length > 0) {
      return [formatted, context];
    }
    return [formatted];
  }
}
